import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PropsWithChildren, useId, useMemo, useState } from 'react';
import { Box, Divider, Drawer, InputAdornment, List, ListItem, ListItemText, Popover, useMediaQuery, useTheme } from '@mui/material';
import SearchTwoTone from '@mui/icons-material/SearchTwoTone';
import TextField from '@forms/Components/TextInput';
import { QueryListItem } from './QueryListItem';
import { useFetchBoardQuery } from '@api/Trinium/Endpoints/BoardQueries/slice';
import { defaultAllOpenQuery, isActiveQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import { createSelector } from '@reduxjs/toolkit';
import { BoardQuery } from '@api/Trinium/types';
import { QueryWithCount } from './QueryWithCount';

import styles from './QuerySelector.module.css';

/**
 * Display of the currently selected query and count of distinct consignments. Clicking the display will open a popover to allow switching between queries.
 */
export default function QuerySelector(props: { terminalNumber: string; currentQuery: BoardQuery }) {
  const uniqueId = useId();

  const [anchor, setAnchor] = useState<null | HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchText, setSearchText] = useState('');

  /** RTK Selector for filtering the queries by the search text input */
  const selectFilteredQueries = useMemo(() => {
    const noLegs: BoardQuery[] = [];

    return createSelector(
      (queries: BoardQuery[] | null | undefined) => queries,
      (_: BoardQuery[] | null | undefined, searchInput: string) => searchInput,
      (queries, searchInput) => {
        if (!queries) return noLegs;

        const filtered = queries.filter(
          (qry) => isActiveQuery(qry) && (!searchInput || qry.name.toLowerCase().includes(searchInput.toLowerCase()))
        );

        return filtered.sort(({ name: left }, { name: right }) => left.localeCompare(right, 'en', { sensitivity: 'base' }));
      }
    );
  }, []);

  const { queries, favoriteQueries, defaultQuery } = useFetchBoardQuery(props.terminalNumber, {
    selectFromResult: ({ data = [] }) => ({
      queries: selectFilteredQueries(data, searchText),
      favoriteQueries: [...data.filter((qry) => qry.isFavorite && isActiveQuery(qry))].sort(({ name: left }, { name: right }) =>
        left.localeCompare(right, 'en', {
          sensitivity: 'base'
        })
      ),
      defaultQuery: data.find((qry) => qry.isDefault && isActiveQuery(qry))
    })
  });

  const onMenuClose = () => {
    setSearchText('');
    setAnchor(null);
  };

  return (
    <>
      <div
        id={uniqueId}
        className={styles.input}
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}>
        <QueryWithCount
          terminalNumber={props.terminalNumber}
          query={props.currentQuery}
        />
        <ArrowDropDownIcon
          fontSize='small'
          className={styles.icon}
        />
      </div>

      <QuerySelectorContainer
        anchor={anchor}
        closeMenu={onMenuClose}
        isMobile={isMobile}>
        <div className={styles['search-container']}>
          <TextField
            placeholder='Filter Board Queries'
            className={styles['search-input']}
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
            }}
            autoFocus
            customProps={{
              labelText: '',
              isDirty: false,
              onClear: () => {
                setSearchText('');
              },
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchTwoTone />
                </InputAdornment>
              )
            }}
          />
        </div>

        <Divider />

        <div className={styles['query-lists']}>
          <span className={styles['group-title']}>Default</span>

          <List disablePadding>
            {defaultQuery ? (
              <QueryListItem
                key={`board-query-${String(defaultQuery.id)}`}
                terminalNumber={props.terminalNumber}
                query={defaultQuery}
                isSelected={defaultQuery.id === props.currentQuery.id}
                closeMenu={onMenuClose}
                isDefaultQuery={true}
              />
            ) : (
              <QueryListItem
                key={`all-query-${String(defaultAllOpenQuery.id)}`}
                terminalNumber={props.terminalNumber}
                query={defaultAllOpenQuery}
                isSelected={defaultAllOpenQuery.id === props.currentQuery.id}
                closeMenu={onMenuClose}
                isDefaultQuery={true}
              />
            )}
          </List>

          <span className={styles['group-title']}>Favorites</span>

          <List disablePadding>
            {favoriteQueries.length === 0 ? (
              <ListItem className={styles.empty}>
                <ListItemText>No Available Favorite Queries</ListItemText>
              </ListItem>
            ) : (
              favoriteQueries.map((query) => (
                <QueryListItem
                  key={`board-query-${String(query.id)}`}
                  terminalNumber={props.terminalNumber}
                  query={query}
                  isSelected={query.id === props.currentQuery.id}
                  closeMenu={onMenuClose}
                  isDefaultQuery={query.id === defaultQuery?.id}
                />
              ))
            )}
          </List>

          <span className={styles['group-title']}>All Queries</span>

          <List disablePadding>
            <QueryListItem
              key={`all-query-${String(defaultAllOpenQuery.id)}`}
              terminalNumber={props.terminalNumber}
              query={defaultAllOpenQuery}
              isSelected={defaultAllOpenQuery.id === props.currentQuery.id}
              closeMenu={onMenuClose}
              isDefaultQuery={!defaultQuery}
            />

            {queries
              .filter((qry) => qry.id > 0)
              .map((query) => (
                <QueryListItem
                  key={`all-query-${String(query.id)}`}
                  terminalNumber={props.terminalNumber}
                  query={query}
                  isSelected={query.id === props.currentQuery.id}
                  closeMenu={onMenuClose}
                  isDefaultQuery={query.id === defaultQuery?.id}
                />
              ))}
          </List>
        </div>
      </QuerySelectorContainer>
    </>
  );
}

/**
 * Component used for rendering mobile/desktop container to allow different functionality.
 */
function QuerySelectorContainer(
  props: PropsWithChildren & {
    anchor: HTMLDivElement | null;
    closeMenu: () => void;
    isMobile: boolean;
  }
) {
  const open = Boolean(props.anchor);

  // Try to find the appbar and align with the bottom of it. Default to app bar height.
  // There seems to be a render race where this component is happening before the Appbar exists.
  const appBar = document.getElementsByTagName('header');
  const appBarHeight = `${String(appBar.length > 0 ? appBar[0].getBoundingClientRect().bottom : 48)}px`;

  if (props.isMobile) {
    return (
      <Drawer
        anchor='top'
        open={open}
        onClose={props.closeMenu}
        slotProps={{
          paper: {
            className: styles['drawer-paper']
          }
        }}
        transitionDuration={300}>
        <Box
          role='presentation'
          className={styles['drawer-menu']}
          sx={{ marginTop: appBarHeight }}>
          {props.children}
        </Box>
      </Drawer>
    );
  }

  return (
    <Popover
      open={open}
      onClose={props.closeMenu}
      anchorEl={props.anchor}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      slotProps={{
        paper: {
          className: styles.menu,
          sx: { height: `calc(80% - ${appBarHeight})` }
        }
      }}
      transitionDuration={300}>
      {props.children}
    </Popover>
  );
}
